import type { ValueFormat } from '@components/Setting/SettingProcessorConfigField';
import type {
  BankSortBy,
  IntermediaryAnalyticsDataResponse,
  ScatterDataPoint,
} from '@models/intermediaryAnalytics';
import type { Setting } from '@models/setting';

const MIN_BUBBLE_SIZE = 7;
const MAX_BUBBLE_SIZE = 40;

export function getBankNameFromChartClick(params: {
  componentType?: string;
  value?: unknown;
  name?: unknown;
}): string {
  if (params.componentType !== 'xAxis') return '';
  return typeof params.value === 'string'
    ? params.value
    : typeof params.name === 'string'
      ? params.name
      : '';
}

function buildScatterData(
  data: IntermediaryAnalyticsDataResponse,
  intermediaryIds: string[],
  intermediaryToIndex: Record<string, number>,
  bankToIndex: Record<string, number>,
  scaleSize: (count: number) => number,
  intermediaryDisplayNameById: ValueFormat,
): ScatterDataPoint[] {
  const result: ScatterDataPoint[] = [];

  for (const intermediaryId of intermediaryIds) {
    const intermediaryIdx = intermediaryToIndex[intermediaryId];
    if (intermediaryIdx === undefined) continue;

    for (const item of data.data[intermediaryId]) {
      const bankIdx = bankToIndex[item.issuerName];
      if (bankIdx === undefined) continue;
      const intermediaryName = intermediaryDisplayNameById[intermediaryId]?.label ?? intermediaryId;

      result.push({
        value: [bankIdx, intermediaryIdx, item.successRate],
        symbolSize: scaleSize(item.totalCount),
        issuerName: item.issuerName,
        totalCount: item.totalCount,
        completedCount: item.completedCount,
        failedCount: item.failedCount,
        successRate: item.successRate,
        intermediaryName,
        isBestIssuer: item.isBestIssuer ?? false,
      });
    }
  }

  return result;
}

function formatTooltip(d: ScatterDataPoint): string {
  const bestBadge = d.isBestIssuer
    ? '<div style="margin-top: 4px; margin-bottom: 8px; padding: 6px 10px; background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border-radius: 6px; color: #92400e; font-weight: 700; font-size: 13px; border: 1px solid #f59e0b;">✦ Best for this processor</div>'
    : '';
  return `
    <div style="padding: 4px; min-width: 200px;">
      <div style="font-weight: 600; margin-bottom: 8px;">${d.issuerName}</div>
      <div style="color: #666;">${d.intermediaryName}</div>
      ${bestBadge}
      <div style="margin-top: 8px; font-size: 13px;">
        <div>Total count: <strong>${d.totalCount}</strong></div>
        <div>Completed: <strong>${d.completedCount}</strong></div>
        <div>Failed: <strong>${d.failedCount}</strong></div>
        <div>Success rate: <strong>${d.successRate.toFixed(2)}%</strong></div>
      </div>
    </div>
  `;
}

function buildIntermediaryDisplayNameMap(setting?: Setting): ValueFormat {
  const values = setting?.setting?.value;

  if (typeof values !== 'object' || values === null || Array.isArray(values)) return {};

  return values as ValueFormat;
}

const CHART_ROW_HEIGHT_SMALL = 55;
const CHART_ROW_HEIGHT_LARGE = 85;

function applyBestIssuerVisualization(
  scatterData: ScatterDataPoint[],
  isLargeScreen: boolean,
): Array<
  ScatterDataPoint & {
    itemStyle?: object;
    symbolSize?: number;
    label?: object;
  }
> {
  const glowStyle = {
    shadowBlur: 14,
    shadowColor: 'rgba(102,155,94, 1)',
    borderWidth: 5,
  };

  const label = {
    show: true,
    formatter: '✦ BEST',
    position: 'top' as const,
    fontSize: isLargeScreen ? 14 : 10,
    fontWeight: 'bold',
    color: '#92400e',
    textBorderColor: '#fef3c7',
    textBorderWidth: 2,
    distance: 1,
  };
  return scatterData.map((d) => {
    if (!d.isBestIssuer) return d;
    const largerSize = Math.max(d.symbolSize * 1.25, d.symbolSize + 4);
    return { ...d, symbolSize: largerSize, label, itemStyle: glowStyle };
  });
}

export function buildChartOption(
  data: IntermediaryAnalyticsDataResponse,
  sortBy: BankSortBy = 'alphabetical',
  processorConfig: Setting | undefined,
  isLargeScreen: boolean,
  filterByBrand: string,
) {
  const rowHeight = isLargeScreen ? CHART_ROW_HEIGHT_LARGE : CHART_ROW_HEIGHT_SMALL;
  const intermediaryIdsRaw = Object.keys(data.data);
  const intermediaryDisplayNameById: ValueFormat = buildIntermediaryDisplayNameMap(processorConfig);

  const intermediaryIds =
    filterByBrand === 'all'
      ? intermediaryIdsRaw
      : intermediaryIdsRaw.filter(
          (id) => intermediaryDisplayNameById[id]?.brandCode === filterByBrand,
        );

  const intermediaries = intermediaryIds.map((id) => intermediaryDisplayNameById[id]?.label ?? id);

  const bankSet = new Set<string>();
  const bankTotals: Record<string, number> = {};
  for (const key of intermediaryIds) {
    for (const item of data.data[key]) {
      bankSet.add(item.issuerName);
      bankTotals[item.issuerName] = (bankTotals[item.issuerName] ?? 0) + item.totalCount;
    }
  }
  const banks = Array.from(bankSet).sort((a, b) => {
    if (sortBy === 'totalPaymentCount') {
      const countDiff = (bankTotals[b] ?? 0) - (bankTotals[a] ?? 0);
      if (countDiff !== 0) return countDiff;
    }
    return a.localeCompare(b);
  });

  const intermediaryToIndex = Object.fromEntries(intermediaryIds.map((id, i) => [id, i]));
  const bankToIndex = Object.fromEntries(banks.map((name, i) => [name, i]));

  const maxCount = Math.max(
    ...intermediaryIds.flatMap((key) => data.data[key].map((item) => item.totalCount)),
    1,
  );
  const scaleSize = (count: number) =>
    count === 0 ? 0 : MIN_BUBBLE_SIZE + (count / maxCount) * (MAX_BUBBLE_SIZE - MIN_BUBBLE_SIZE);

  const scatterData = buildScatterData(
    data,
    intermediaryIds,
    intermediaryToIndex,
    bankToIndex,
    scaleSize,
    intermediaryDisplayNameById,
  );
  const seriesData = applyBestIssuerVisualization(scatterData, isLargeScreen);

  return {
    chartHeight: Math.max(500, rowHeight * intermediaries.length),
    option: {
      tooltip: {
        trigger: 'item',
        formatter: (params: { data: ScatterDataPoint }) => formatTooltip(params.data),
        confine: true,
      },
      grid: {
        left: 0,
        right: 120,
        top: 40,
        bottom: 150,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: banks,
        nameGap: 50,
        triggerEvent: true,
        axisLabel: {
          rotate: 45,
          fontSize: isLargeScreen ? 16 : 10,
          overflow: 'truncate',
          width: 100,
          interval: 0,
          hideOverlap: false,
        },
        tooltip: {
          show: true,
          position: 'top',
          formatter: (params: { name?: string }) => params.name ?? '',
        },
        splitLine: { show: true, lineStyle: { color: '#eee', type: 'dashed' } },
      },
      yAxis: {
        type: 'category',
        data: intermediaries,
        nameGap: 60,
        axisLabel: {
          fontSize: isLargeScreen ? 16 : 10,
          formatter: (value: string) => {
            const normalValue = `  {normal|${value}}`;
            if (filterByBrand !== 'all') return normalValue;

            const originalValue = intermediaryIds.find(
              (id) => intermediaryDisplayNameById[id]?.label === value,
            );

            if (!originalValue) return normalValue;

            const brandCode = intermediaryDisplayNameById[originalValue]?.brandCode ?? '';
            if (brandCode) {
              return `{bold|${brandCode}}: {normal|${value}}`;
            }

            return normalValue;
          },
          rich: { bold: { fontWeight: 'bold', fontSize: isLargeScreen ? 16 : 12 }, normal: {} },
        },
        splitLine: { lineStyle: { color: '#eee', type: 'dashed' } },
      },
      dataZoom: [
        {
          type: 'slider',
          xAxisIndex: 0,
          height: 40,
          handleSize: '100%',
          showDataShadow: false,
          borderRadius: 8,
          borderColor: '#e5e7eb',
          backgroundColor: '#f3f4f6',
          fillerColor: 'rgba(76, 175, 80, 0.2)',
          handleStyle: {
            borderColor: '#66BB6A',
            shadowColor: 'rgba(76, 175, 80, 0.3)',
            shadowOffsetY: 2,
          },
          moveHandleStyle: {
            color: '#81C784',
            borderColor: '#66BB6A',
            borderWidth: 1,
          },
          emphasis: {
            handleStyle: {
              borderColor: '#4CAF50',
              borderWidth: 2,
              shadowBlur: 8,
              shadowColor: 'rgba(76, 175, 80, 0.4)',
            },
            moveHandleStyle: {
              color: '#66BB6A',
              borderColor: '#4CAF50',
            },
          },
          start: 0,
          end: Math.min(100, (40 / banks.length) * 100),
          minSpan: Math.min(100, (20 / banks.length) * 100),
          bottom: 100,
        },
      ],
      visualMap: {
        type: 'continuous',
        min: 0,
        max: 100,
        calculable: true,
        orient: 'vertical',
        right: 10,
        top: 'center',
        inRange: {
          color: ['#D32F2F', '#FBC02D', '#2E7D32'],
        },
      },
      series: [
        {
          type: 'scatter',
          symbol: 'circle',
          data: seriesData,
          emphasis: {
            scale: 1.1,
            itemStyle: {
              borderColor: '#333',
              borderWidth: 1,
            },
          },
        },
      ],
    },
  };
}
